import styled, { css, keyframes } from 'styled-components';
import { colors } from '../../../theme/colors';
import { fonts } from '../../../theme/fonts';
import { sizes } from '../../../theme/sizes';
import { breakpoints } from '../../../theme/breakpoints';

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;

const fadeSlideOut = keyframes`
  from {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateX(-30px) scale(0.95);
  }
`;

type AnimationPhase = 'entering' | 'visible' | 'exiting';

export const ContentContainer = styled.div<{ $animationPhase: AnimationPhase }>`
  width: 100%;
  background: transparent;
  padding: ${sizes.spacing.xl} 0;

  ${({ $animationPhase }) => {
    if ($animationPhase === 'entering') {
      return css`
        animation: ${fadeSlideIn} 500ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
      `;
    }
    if ($animationPhase === 'exiting') {
      return css`
        animation: ${fadeSlideOut} 500ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
      `;
    }
    return css`
      opacity: 1;
      transform: translateX(0) scale(1);
    `;
  }}

  ${breakpoints.media.mobile} {
    padding: ${sizes.spacing.lg} 0;
  }
`;

export const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${sizes.spacing.xl};

  ${breakpoints.media.mobile} {
    padding: 0 ${sizes.spacing.md};
  }
`;

export const ContentParagraph = styled.p<{ $align?: 'left' | 'center' | 'right' }>`
  color: ${colors.contentText};
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: ${fonts.sizes.md};
  line-height: 1.7;
  margin-bottom: ${sizes.spacing.md};
  text-align: ${({ $align }) => $align || 'left'};

  &:last-child {
    margin-bottom: 0;
  }

  ${breakpoints.media.desktop} {
    font-size: ${fonts.sizes.desktopMd};
    line-height: 1.8;
  }

  ${breakpoints.media.mobile} {
    font-size: ${fonts.sizes.sm};
    line-height: 1.6;
  }
`;

export const ContentHeading = styled.h2<{ $align?: 'left' | 'center' | 'right' }>`
  color: ${colors.cyan};
  font-family: ${fonts.families.primary};
  font-size: ${fonts.sizes.xxl};
  font-weight: ${fonts.weights.bold};
  line-height: 1.3;
  margin: ${sizes.spacing.xl} 0 ${sizes.spacing.lg};
  text-align: ${({ $align }) => $align || 'left'};
  text-transform: uppercase;
  letter-spacing: ${fonts.letterSpacing.wide};

  &:first-child {
    margin-top: 0;
  }

  ${breakpoints.media.desktop} {
    font-size: ${fonts.sizes.desktopXl};
  }

  ${breakpoints.media.mobile} {
    font-size: ${fonts.sizes.xl};
    margin: ${sizes.spacing.lg} 0 ${sizes.spacing.md};
  }
`;

export const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  margin: ${sizes.spacing.lg} 0;
  border-radius: ${sizes.borderRadius.medium};
  box-shadow: 0 4px 16px ${colors.blackTransparent[50]};

  ${breakpoints.media.mobile} {
    margin: ${sizes.spacing.md} 0;
    border-radius: ${sizes.borderRadius.small};
  }
`;

export const ContentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${sizes.spacing.lg} 0;

  ${breakpoints.media.mobile} {
    margin: ${sizes.spacing.md} 0;
  }
`;

export const ContentListItem = styled.li`
  color: ${colors.contentText};
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: ${fonts.sizes.md};
  line-height: 1.7;
  padding-left: ${sizes.spacing.lg};
  margin-bottom: ${sizes.spacing.sm};
  position: relative;

  &::before {
    content: '▪';
    color: ${colors.primary};
    font-size: ${fonts.sizes.lg};
    position: absolute;
    left: 0;
    top: 0;
    line-height: 1.7;
  }

  ${breakpoints.media.desktop} {
    font-size: ${fonts.sizes.desktopMd};
    line-height: 1.8;

    &::before {
      font-size: 1.4rem;
    }
  }

  ${breakpoints.media.mobile} {
    font-size: ${fonts.sizes.sm};
    padding-left: ${sizes.spacing.md};
  }
`;

export const ContentBold = styled.strong`
  font-weight: ${fonts.weights.bold};
  color: ${colors.white};
`;

export const ContentItalic = styled.em`
  font-style: italic;
`;

export const ContentSpacer = styled.div`
  height: ${sizes.spacing.md};
`;
